import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import qs from 'query-string'

import {
  errors, direction, paymentLabel, basicPlanPaymentCopy,
  standardPlanPaymentCopy, annualPlanPaymentCopy
} from '../constants'
import { usePlanChangeFlag } from '../../../hooks/usePlanChange'
import Payment from '../screens/payment'
import Error from '../screens/error'
import Voucher from '../screens/voucher'
import { modalContent } from '../../shared/subscription-constants'

/**
 * Hook to redirect to change subscription screens based on url parameters
 */

const useChangeSubscriptionScreen = (changeDirection, modalCloseTarget) => {
  const location = useLocation()
  const history = useHistory()
  const planDetails = usePlanChangeFlag()

  const products = {
    annual: {
      ...planDetails.annual,
      copy: annualPlanPaymentCopy,
      details: [
        planDetails.annual.staticDetail,
        {
          name: paymentLabel,
          value: `$${planDetails.annual.price}`
        }
      ]
    },
    standard: {
      ...planDetails.standard,
      copy: standardPlanPaymentCopy,
      details: [
        planDetails.standard.staticDetail,
        {
          name: paymentLabel,
          value: `$${planDetails.standard.price}`
        }
      ]
    },
    basic: {
      ...planDetails.basic,
      copy: basicPlanPaymentCopy,
      details: [
        planDetails.basic.staticDetail,
        {
          name: paymentLabel,
          value: `$${planDetails.basic.price}`
        }
      ]
    }
  }

  const {
    plan,
    content,
    errorCode,
    orderId,
    voucher
  } = qs.parse(location.search)

  if (
    changeDirection !== direction.upgrade &&
    changeDirection !== direction.downgrade
  ) {
    history.push({
      ...location,
      search: qs.stringify({
        ...qs.parse(location.search),
        errorCode: errors.modalParemetersInvalid.errorCode
      })
    })
  }

  const closeModalAndRedirect = () => {
    history.replace({
      pathname: modalCloseTarget
    })
  }

  const getErrorScreen = () => {
    const error =
      Object.values(errors).find(e => e.errorCode === errorCode) ||
      errors.modalParemetersInvalid
    return <Error error={error} onStartOver={closeModalAndRedirect} />
  }

  const getPaymentScreen = () => {
    const product = Object.values(products).find(p => p.sku === plan)
    return <Payment product={product} changeDirection={changeDirection} voucher={voucher} />
  }

  const getVoucherScreen = () => {
    const product = Object.values(products).find(p => p.sku === plan)
    return <Voucher product={product} changeDirection={changeDirection} />
  }

  const getScreenToDisplay = () => {
    // Error cases
    if (!plan || errorCode) {
      return getErrorScreen()
    }

    const isPaymentStep = content === modalContent.payment
    const isUpgrade = changeDirection === direction.upgrade
    const isDowngrade = changeDirection === direction.downgrade

    // Show voucher screen for upgrades until the flow moves to payment
    if (isUpgrade && !isPaymentStep) {
      return getVoucherScreen()
    }

    // Show payment screen after voucher flow or for downgrades
    if (isPaymentStep || isDowngrade) {
      return getPaymentScreen()
    }

    return null
  }

  useEffect(() => {
    // Redirect if order completed
    if (orderId) {
      closeModalAndRedirect()
    }
  }, [orderId])

  return orderId ? null : getScreenToDisplay()
}

export default useChangeSubscriptionScreen
